import { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { loadCrewIdentity } from '../crewAccess';
import { verificationFailure, VerificationStage } from '../sessionRecovery';

type Recovery = { reason: 'offline' | 'temporary' | 'unavailable'; retrying: boolean };
const RETRY_DELAYS = [1000, 2000, 4000];
const failure = (code: string) => Object.assign(new Error('Session verification failed.'), { code });

export function useAdminSession() {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [checking, setChecking] = useState(true);
  const [recovery, setRecovery] = useState<Recovery | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const generation = useRef(0);
  const verifiedIdentity = useRef<string | null>(null);
  const [verifiedAuthId, setVerifiedAuthId] = useState<string | null>(null);
  const retryAction = useRef<() => void>(() => {});
  const stopRestoration = useRef<() => void>(() => {});
  const loggingOut = useRef(false);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    let inFlightGeneration = 0;
    let pending: { background: boolean; expectedIdentity?: string } | null = null;
    let recovering = false;
    let retryCount = 0;
    let expectedIdentity: string | undefined;
    const invalidatePendingRestoration = () => { ++generation.current; };
    const clearTimer = () => { clearTimeout(timer); timer = undefined; };
    const clearIdentity = () => {
      clearTimeout(expiryTimer); expiryTimer = undefined;
      verifiedIdentity.current = null;
      setVerifiedAuthId(null);
      setCurrentUser(null);
    };
    const stop = () => {
      ++generation.current;
      clearTimer(); pending = null; recovering = false; retryCount = 0;
      expectedIdentity = undefined;
      clearIdentity(); setRecovery(null); setAccessDenied(false);
    };
    stopRestoration.current = stop;

    const restore = async (background = false, expected?: string) => {
      if (!active || loggingOut.current) return;
      if (inFlight) { pending = { background, expectedIdentity: expected }; return; }
      inFlight = true;
      const request = ++generation.current;
      inFlightGeneration = request;
      const latest = () => active && !loggingOut.current && request === generation.current;
      let stage: VerificationStage = 'session';
      if (recovering) setRecovery(previous => previous && { ...previous, retrying: true });
      else if (!background) setChecking(true);
      try {
        if (navigator.onLine === false) throw Object.assign(new Error('Connection unavailable.'), { name: 'NetworkError' });
        const { data, error } = await supabase.auth.getSession();
        if (!latest()) return;
        if (error) throw error;
        if (!data.session) throw failure('session_missing');
        expectedIdentity = expected || data.session.user.id;
        stage = 'user';
        // Validate with Auth, never grant access from a persisted token alone.
        const verified = await supabase.auth.getUser();
        if (!latest()) return;
        if (verified.error) throw verified.error;
        if (!verified.data.user) throw failure('session_missing');
        if (verified.data.user.id !== data.session.user.id ||
          (expected && verified.data.user.id !== expected) ||
          (background && verified.data.user.id !== verifiedIdentity.current)) throw failure('identity_changed');
        // getSession refreshes expired access tokens. Never accept a still-expired result.
        if (typeof data.session.expires_at === 'number' && data.session.expires_at * 1000 <= Date.now()) throw failure('jwt_expired');
        stage = 'identity';
        const profile = await loadCrewIdentity();
        if (!latest()) return;
        if (!profile || profile.role !== 'Admin') throw failure('admin_required');
        if (typeof data.session.expires_at === 'number' && data.session.expires_at * 1000 <= Date.now()) throw failure('jwt_expired');
        recovering = false; retryCount = 0; expectedIdentity = undefined;
        verifiedIdentity.current = verified.data.user.id;
        setVerifiedAuthId(verified.data.user.id);
        setCurrentUser(profile); setRecovery(null); setAccessDenied(false);
        if (typeof data.session.expires_at === 'number') {
          clearTimeout(expiryTimer);
          const remaining = data.session.expires_at * 1000 - Date.now();
          expiryTimer = setTimeout(() => {
            expiryTimer = undefined;
            if (!active || loggingOut.current) return;
            ++generation.current; clearTimer(); clearIdentity(); setChecking(true);
            void restore(false, verified.data.user!.id);
          }, Math.min(remaining, 2147483647));
        }
      } catch (error) {
        if (!latest()) return;
        const kind = verificationFailure(error, stage);
        clearIdentity(); setChecking(false);
        if (kind === 'invalid' || kind === 'denied') {
          recovering = false; retryCount = 0; expectedIdentity = undefined;
          clearTimer(); setRecovery(null); setAccessDenied(kind === 'denied');
        } else {
          recovering = true; expectedIdentity = expected || expectedIdentity;
          setRecovery({ reason: navigator.onLine === false ? 'offline' : kind === 'retryable' ? 'temporary' : 'unavailable', retrying: false });
          setAccessDenied(false);
          if (kind === 'retryable' && navigator.onLine !== false && retryCount < RETRY_DELAYS.length) {
            const delay = RETRY_DELAYS[retryCount++];
            timer = setTimeout(() => {
              timer = undefined;
              if (latest() && recovering) void restore(false, expectedIdentity);
            }, delay);
          }
        }
      } finally {
        if (latest()) setChecking(false);
        inFlight = false;
        if (active && !loggingOut.current && pending) {
          const next = pending; pending = null;
          clearTimer();
          void restore(next.background, next.expectedIdentity);
        }
      }
    };

    const retry = () => {
      if (!active || !recovering || pending || loggingOut.current) return;
      if (inFlight) {
        // Reconnect may arrive before an invalidated offline request finishes.
        if (inFlightGeneration !== generation.current) pending = { background: false, expectedIdentity };
        return;
      }
      clearTimer(); retryCount = 0;
      void restore(false, expectedIdentity);
    };
    retryAction.current = retry;
    const offline = () => {
      if (loggingOut.current || (!verifiedIdentity.current && !recovering && !inFlight)) return;
      expectedIdentity = verifiedIdentity.current || expectedIdentity;
      ++generation.current; clearTimer(); pending = null;
      recovering = true; clearIdentity(); setChecking(false); setAccessDenied(false);
      setRecovery({ reason: 'offline', retrying: false });
    };
    const online = () => { if (recovering) retry(); };
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active || loggingOut.current) return;
      ++generation.current; clearTimer();
      if (!session) { stop(); setChecking(false); return; }
      const identity = session.user.id;
      const unexpired = typeof session.expires_at !== 'number' || session.expires_at * 1000 > Date.now();
      const background = !recovering && unexpired && !!verifiedIdentity.current && identity === verifiedIdentity.current;
      // A different account must not inherit either recovery or the old Admin's UI.
      if (recovering && expectedIdentity && identity !== expectedIdentity) {
        recovering = false; retryCount = 0; setRecovery(null);
      }
      expectedIdentity = identity;
      if (!background) { clearIdentity(); if (!recovering) setChecking(true); }
      setAccessDenied(false);
      // Auth callbacks stay synchronous; calls begin outside the SDK's lock.
      pending = null;
      timer = setTimeout(() => { timer = undefined; void restore(background, identity); }, 0);
    });
    void restore();
    return () => {
      active = false; invalidatePendingRestoration(); clearTimer(); pending = null;
      clearTimeout(expiryTimer);
      retryAction.current = () => {}; stopRestoration.current = () => {};
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
      data.subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    if (loggingOut.current) return;
    loggingOut.current = true;
    stopRestoration.current(); setChecking(true);
    try {
      // Preserve the existing logout scope; recovery never calls signOut itself.
      const { error } = await supabase.auth.signOut();
      if (error) throw new Error('Sign out could not be completed. Please retry.');
    } finally { loggingOut.current = false; setChecking(false); }
  };
  return { currentUser, verifiedAuthId, checking, recovery, accessDenied, retry: () => retryAction.current(), logout };
}
